// src/pages/NotFoundPage.jsx
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Home,
  Search,
  Video,
  Calendar,
  Users,
  MapPin,
} from "lucide-react";

export default function NotFoundPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const quickLinks = [
    {
      icon: Home,
      label: "Home",
      desc: "Back to the main page",
      path: "/",
    },
    {
      icon: Video,
      label: "UserMeet",
      desc: "Join or create video meetings",
      path: "/user-meet",
    },
    {
      icon: Calendar,
      label: "Events",
      desc: "Browse upcoming events",
      path: "/events",
    },
    // {
    //   icon: Users,
    //   label: "Network",
    //   desc: "Connect with professionals",
    //   path: "/network",
    // },
    {
      icon: MapPin,
      label: "Lobby",
      desc: "Explore the lobby",
      path: "/lobby",
    },
  ];

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
      }}
    >
      <div className="max-w-2xl w-full text-center">
        {/* ── 404 Visual ── */}
        <div className="mb-10">
          {/* Animated 404 text */}
          <div className="relative inline-block mb-6">
            <h1
              className="text-[120px] md:text-[180px] font-light tracking-tighter leading-none text-transparent bg-clip-text select-none"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                backgroundImage:
                  "linear-gradient(135deg, #C6A87C 0%, #8A6F4A 40%, #C6A87C 60%, #D9BC93 100%)",
              }}
            >
              404
            </h1>

            {/* Glow behind the text */}
            <div
              className="absolute inset-0 blur-3xl opacity-20 -z-10"
              style={{
                background:
                  "radial-gradient(ellipse at center, #C6A87C 0%, transparent 70%)",
              }}
            />
          </div>

          {/* Message */}
          <h2
            className="text-3xl md:text-4xl font-light tracking-tight text-white mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Page not{" "}
            <span className="italic text-[#C6A87C]">found</span>
          </h2>

          <p className="text-white/50 text-sm max-w-md mx-auto leading-relaxed mb-2">
            The page you're looking for doesn't exist or may have been moved.
            Check the URL or navigate back to a page you know.
          </p>

          {/* Show the attempted path */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/[0.08] mt-3">
            <Search size={12} className="text-white/30" />
            <span className="text-xs font-mono text-white/40 max-w-[300px] truncate">
              {location.pathname}
            </span>
          </div>
        </div>

        {/* ── Action Buttons ── */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/[0.12] text-white/70 text-sm font-medium hover:border-white/30 hover:text-white hover:bg-white/[0.04] transition-all group"
          >
            <ArrowLeft
              size={16}
              className="group-hover:-translate-x-0.5 transition-transform"
            />
            Go Back
          </button>

          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#C6A87C] text-[#061412] text-sm font-semibold hover:bg-[#D9BC93] transition-colors"
          >
            <Home size={16} />
            Go Home
          </button>
        </div>

        {/* ── Quick Links ── */}
        <div>
          <p className="text-[10px] font-semibold tracking-widest uppercase text-white/30 mb-5">
            Quick Links
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-3 max-w-lg mx-auto md:max-w-none">
            {quickLinks.map((link) => {
              const Icon = link.icon;
              return (
                <button
                  key={link.path}
                  onClick={() => navigate(link.path)}
                  className="group flex items-center gap-3 p-4 rounded-xl border border-white/[0.06] bg-white/[0.02] text-left hover:border-[#C6A87C]/30 hover:bg-[#C6A87C]/[0.04] transition-all"
                >
                  <div className="w-9 h-9 rounded-lg bg-white/[0.05] border border-white/[0.08] flex items-center justify-center flex-shrink-0 group-hover:border-[#C6A87C]/30 group-hover:bg-[#C6A87C]/10 transition-all">
                    <Icon
                      size={16}
                      className="text-white/40 group-hover:text-[#C6A87C] transition-colors"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white/80 group-hover:text-white transition-colors">
                      {link.label}
                    </p>
                    <p className="text-[11px] text-white/35 truncate">
                      {link.desc}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Meeting Link Helper ── */}
        <div className="mt-10 p-5 rounded-2xl border border-[#C6A87C]/15 bg-[#C6A87C]/[0.04] max-w-md mx-auto">
          <div className="flex items-start gap-3 text-left">
            <div className="w-9 h-9 rounded-lg bg-[#C6A87C]/15 border border-[#C6A87C]/25 flex items-center justify-center flex-shrink-0">
              <Video size={16} className="text-[#C6A87C]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#C6A87C] mb-1">
                Looking for a meeting?
              </p>
              <p className="text-xs text-white/50 leading-relaxed mb-3">
                If you received a meeting link or join code, you can enter it
                below to join directly.
              </p>
              <MeetingJoinInput />
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <p className="mt-12 text-xs text-white/20">
          © {new Date().getFullYear()} VMeetio. All rights reserved.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline meeting join input
// ---------------------------------------------------------------------------
function MeetingJoinInput() {
  const navigate = useNavigate();
  const [code, setCode] = React.useState("");
  const [joining, setJoining] = React.useState(false);

  const handleJoin = (e) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;

    setJoining(true);

    // Extract join code from various formats:
    // Full URL: https://vmeetio.com/meet/tga-sf85-7inh
    // Full URL: https://vmeetio.com/user-meet/join/tga-sf85-7inh
    // Just code: tga-sf85-7inh
    let joinCode = trimmed;

    try {
      const url = new URL(trimmed);
      const parts = url.pathname.split("/").filter(Boolean);

      // /meet/:code or /user-meet/join/:code
      if (parts[0] === "meet" && parts[1]) {
        joinCode = parts[1];
      } else if (
        parts[0] === "user-meet" &&
        parts[1] === "join" &&
        parts[2]
      ) {
        joinCode = parts[2];
      }
    } catch {
      // Not a URL — use as-is (just the join code)
    }

    navigate(`/user-meet/join/${joinCode}`);
  };

  return (
    <form onSubmit={handleJoin} className="flex gap-2">
      <input
        type="text"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder="Paste link or join code"
        className="flex-1 bg-white/[0.06] border border-white/[0.12] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#C6A87C]/50 focus:outline-none transition-colors font-mono"
      />
      <button
        type="submit"
        disabled={!code.trim() || joining}
        className="px-4 py-2 rounded-lg bg-[#C6A87C] text-[#061412] text-xs font-semibold hover:bg-[#D9BC93] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
      >
        Join
      </button>
    </form>
  );
}